import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import useFormatters from '../lib/useFormatters';
import {
  Briefcase, TrendingUp, TrendingDown, DollarSign, Building2,
  Shield, AlertTriangle, CheckCircle2, Target, ChevronDown,
  ChevronUp, Loader2, ArrowLeft, MapPin, BarChart3, ArrowUpRight,
  ExternalLink, XCircle, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { Footer } from '../components/LegalDisclaimers';
import PortfolioGoals from '../components/PortfolioGoals';
import AIAvatar from '../components/AIAvatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BAND_COLORS = {
  exceptional: { bg: 'bg-emerald-500', text: 'text-emerald-500', light: 'bg-emerald-500/10' },
  strong: { bg: 'bg-green-500', text: 'text-green-500', light: 'bg-green-500/10' },
  good: { bg: 'bg-lime-500', text: 'text-lime-500', light: 'bg-lime-500/10' },
  average: { bg: 'bg-amber-500', text: 'text-amber-500', light: 'bg-amber-500/10' },
  needs_review: { bg: 'bg-orange-500', text: 'text-orange-500', light: 'bg-orange-500/10' },
  high_risk: { bg: 'bg-red-500', text: 'text-red-500', light: 'bg-red-500/10' },
  avoid: { bg: 'bg-red-700', text: 'text-red-700', light: 'bg-red-700/10' },
};

function ScoreRing({ score, size = 100, strokeWidth = 6 }) {
  const radius = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * radius;
  const prog = (score / 100) * circ;
  const color = score >= 70 ? '#45C04A' : score >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor"
          className="text-slate-200 dark:text-slate-700" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color}
          strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={`${prog} ${circ}`}
          style={{ filter: `drop-shadow(0 0 6px ${color}40)`, transition: 'stroke-dasharray 1s ease' }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-2xl font-black" style={{ color }}>{score}</span>
        <span className="text-[8px] font-bold uppercase tracking-widest text-slate-400">/100</span>
      </div>
    </div>
  );
}

function RiskBar({ band, count, percent }) {
  const cfg = BAND_COLORS[band] || BAND_COLORS.avoid;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-500 dark:text-slate-400 w-24 capitalize">{band.replace('_', ' ')}</span>
      <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${cfg.bg}`} style={{ width: `${Math.max(percent, 2)}%`, transition: 'width 0.7s ease' }} />
      </div>
      <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 w-14 text-right">{count} ({percent}%)</span>
    </div>
  );
}

export default function PortfolioDashboard() {
  const { t, i18n } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ ai: true, actions: true, deals: false });
  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/portfolio/health?language=${currentLang}`, { withCredentials: true });
        setData(resp.data);
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error(t('portfolio.proRequired'));
          navigate('/pricing');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate, t, currentLang]);

  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0F1C]">
        <div className="flex items-center justify-end gap-3 px-6 py-3">
          <LanguageSwitcher />
          <DarkModeToggle />
        </div>
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#45C04A] to-[#65D95A] rounded-2xl flex items-center justify-center animate-pulse">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <Loader2 className="w-5 h-5 animate-spin text-[#45C04A]" />
            <p className="text-sm text-slate-500">{t('portfolio.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.empty) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0F1C]">
        <div className="flex items-center justify-end gap-3 px-6 py-3">
          <LanguageSwitcher />
          <DarkModeToggle />
        </div>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <Briefcase className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#12355B] dark:text-white mb-2">{t('portfolio.emptyTitle')}</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">{t('portfolio.emptyDesc')}</p>
          <Button onClick={() => navigate('/analyze')} className="bg-[#45C04A] hover:bg-[#65D95A] text-white rounded-xl px-8">
            {t('portfolio.analyzeFirst')}
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const ov = data.overview;
  const rp = data.risk_profile || {};
  const div = data.diversification || {};
  const ai = data.ai_summary || {};
  const avgColor = ov.avg_score >= 70 ? 'text-green-500' : ov.avg_score >= 50 ? 'text-amber-500' : 'text-red-500';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0F1C]" data-testid="portfolio-dashboard">
      {/* Nav */}
      <div className="flex items-center justify-end gap-3 px-6 py-3">
        <Button
          data-testid="back-to-dashboard-btn"
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="text-slate-500 dark:text-slate-400 hover:text-[#12355B] dark:hover:text-white text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          {t('nav.backToDashboard')}
        </Button>
        <Button data-testid="new-analysis-button" onClick={() => navigate('/analyze')} size="sm"
          className="bg-[#12355B] hover:bg-[#1565C0] text-white dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#0F172A] font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> {t('dashboard.newAnalysis')}
        </Button>
        <LanguageSwitcher />
        <DarkModeToggle />
      </div>

      <div className="relative isolate max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Background Image */}
        <div className="absolute inset-0 -mx-4 sm:-mx-6 overflow-hidden -z-10">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/35251cdc-fe6f-4305-b40a-115bc9637197/images/6e16fd70b2939ca78f5767cfd44afba2c9adfee3f72c527185a2556ae3753ae9.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-50/90 dark:bg-[#0A0F1C]/93 backdrop-blur-[2px]" />
        </div>
        {/* Back + Title + Compare */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
              <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </button>
            <div>
              <h1 className="font-primary font-bold text-2xl text-[#12355B] dark:text-white">{t('portfolio.title')}</h1>
              <p className="text-xs text-slate-400">{data.deal_count} {t('portfolio.properties')} &middot; {data.total_units} {t('portfolio.units')}</p>
            </div>
          </div>
          <Button
            data-testid="compare-scenarios-btn"
            onClick={() => navigate('/portfolio/compare')}
            className="rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white px-5"
          >
            <Target className="w-4 h-4 mr-2" />
            {t('portfolioCompare.title')}
          </Button>
        </div>

        {/* Portfolio Overview Hero */}
        <div className="bg-gradient-to-br from-[#12355B] to-[#1565C0] rounded-2xl p-6 mb-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle at 85% 15%, rgba(0,200,83,0.5) 0%, transparent 50%)' }} />
          <div className="relative grid grid-cols-2 md:grid-cols-5 gap-4 items-center">
            {/* Score */}
            <div className="col-span-2 md:col-span-1 flex flex-col items-center">
              <ScoreRing score={Math.round(ov.avg_score)} size={90} strokeWidth={5} />
              <p className="text-xs text-white/50 mt-1">{t('portfolio.avgScore')}</p>
            </div>
            {/* KPIs */}
            <div className="text-center">
              <p data-testid="hero-kpi-total-value" className="text-2xl font-black text-white">{fmt.isFr ? `${(ov.total_value/1e6).toFixed(2).replace('.',',')} M$` : `$${(ov.total_value/1e6).toFixed(2)}M`}</p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">{t('portfolio.totalValue')}</p>
            </div>
            <div className="text-center">
              <p data-testid="hero-kpi-total-equity" className="text-2xl font-black text-white">{fmt.isFr ? `${(ov.total_equity/1e6).toFixed(2).replace('.',',')} M$` : `$${(ov.total_equity/1e6).toFixed(2)}M`}</p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">{t('portfolio.totalEquity')}</p>
            </div>
            <div className="text-center">
              <p data-testid="hero-kpi-monthly-cf" className={`text-2xl font-black ${ov.total_monthly_cash_flow >= 0 ? 'text-[#45C04A]' : 'text-red-400'}`}>
                {fmt.money0(ov.total_monthly_cash_flow)}
              </p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">{t('portfolio.monthlyCF')}</p>
            </div>
            <div className="text-center">
              <p data-testid="hero-kpi-avg-dscr" className="text-2xl font-black text-white">{fmt.number(ov.avg_dscr)}</p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">{t('portfolio.avgDSCR')}</p>
            </div>
          </div>
          {/* Secondary KPIs */}
          <div className="relative grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
            <div className="text-center">
              <p data-testid="hero-kpi-avg-cap-rate" className="text-sm font-bold text-white">{fmt.percent(ov.avg_cap_rate)}</p>
              <p className="text-[10px] text-white/40">{t('portfolio.avgCapRate')}</p>
            </div>
            <div className="text-center">
              <p data-testid="hero-kpi-avg-roi" className="text-sm font-bold text-white">{fmt.percent(ov.avg_roi)}</p>
              <p className="text-[10px] text-white/40">{t('portfolio.avgROI')}</p>
            </div>
            <div className="text-center">
              <p data-testid="hero-kpi-annual-cf" className="text-sm font-bold text-white">{fmt.money0(ov.total_annual_cash_flow)}</p>
              <p className="text-[10px] text-white/40">{t('portfolio.annualCF')}</p>
            </div>
          </div>
        </div>

        {/* Portfolio Goals */}
        <div className="mb-6">
          <PortfolioGoals />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Portfolio Summary */}
            {ai.summary && (
              <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                <button onClick={() => toggle('ai')} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <AIAvatar size={24} label={t('portfolio.aiSummary')} />
                    <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('portfolio.aiSummary')}</h3>
                  </div>
                  {expanded.ai ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {expanded.ai && (
                  <div className="px-5 pb-5 space-y-4">
                    <p data-testid="portfolio-ai-summary" className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{ai.summary}</p>
                    {ai.strengths?.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider mb-2">{t('portfolio.strengths')}</h5>
                        <ul className="space-y-1">
                          {ai.strengths.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {ai.vulnerabilities?.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider mb-2">{t('portfolio.vulnerabilities')}</h5>
                        <ul className="space-y-1">
                          {ai.vulnerabilities.map((v, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                              <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />{v}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {ai.strategy?.length > 0 && (
                      <div className="bg-violet-50 dark:bg-violet-950/20 rounded-xl p-3">
                        <h5 className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider mb-2">{t('portfolio.strategy')}</h5>
                        <ul className="space-y-1.5">
                          {ai.strategy.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                              <Target className="w-3.5 h-3.5 text-violet-400 mt-0.5 flex-shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Candidates */}
            {data.action_candidates?.length > 0 && (
              <div className="bg-white dark:bg-[#131824] border border-red-200 dark:border-red-800/30 rounded-2xl overflow-hidden">
                <button onClick={() => toggle('actions')} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('portfolio.actionRequired')}</h3>
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/10 text-red-500">{data.action_candidates.length}</span>
                  </div>
                  {expanded.actions ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {expanded.actions && (
                  <div className="px-5 pb-5 space-y-3">
                    {data.action_candidates.map((c) => (
                      <div key={c.deal_id} className="flex items-center justify-between bg-red-50 dark:bg-red-950/10 rounded-xl p-3 border border-red-100 dark:border-red-900/20">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#12355B] dark:text-white truncate">{c.address}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {c.action_reasons.map(r => (
                              <span key={r} className="px-2 py-0.5 text-[10px] rounded-full bg-red-500/10 text-red-500 font-medium">
                                {t(`portfolio.reason_${r}`, r.replace(/_/g, ' '))}
                              </span>
                            ))}
                          </div>
                        </div>
                        <button onClick={() => navigate(`/deal/${c.deal_id}`)}
                          className="ml-3 p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50">
                          <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* All Deals Table */}
            <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
              <button data-testid="deal-performance-toggle" onClick={() => toggle('deals')} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#45C04A]" />
                  <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('portfolio.dealPerformance')}</h3>
                </div>
                {expanded.deals ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {expanded.deals && (
                <div className="px-5 pb-5 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <th className="text-left py-2 text-slate-400 font-semibold">{t('portfolio.property')}</th>
                        <th className="text-right py-2 text-slate-400 font-semibold">{t('portfolio.score')}</th>
                        <th className="text-right py-2 text-slate-400 font-semibold">{t('portfolio.cashFlow')}</th>
                        <th className="text-right py-2 text-slate-400 font-semibold">{t('portfolio.capRate')}</th>
                        <th className="text-right py-2 text-slate-400 font-semibold">DSCR</th>
                        <th className="text-right py-2 text-slate-400 font-semibold">{t('portfolio.units')}</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.deals || []).map(d => {
                        const bc = BAND_COLORS[d.band] || BAND_COLORS.avoid;
                        return (
                          <tr key={d.deal_id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                            <td className="py-2.5 max-w-[200px] truncate text-slate-700 dark:text-slate-300">{d.address}</td>
                            <td className="py-2.5 text-right">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${bc.light} ${bc.text}`}>
                                {d.deal_score}
                              </span>
                            </td>
                            <td className={`py-2.5 text-right font-semibold ${d.monthly_cash_flow >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                              {fmt.money0(d.monthly_cash_flow)}
                            </td>
                            <td className="py-2.5 text-right text-slate-700 dark:text-slate-300">{fmt.percent(d.cap_rate)}</td>
                            <td className={`py-2.5 text-right font-semibold ${d.dscr >= 1 ? 'text-green-600' : 'text-red-500'}`}>{fmt.number(d.dscr)}</td>
                            <td className="py-2.5 text-right text-slate-700 dark:text-slate-300">{d.num_units}</td>
                            <td className="py-2.5">
                              <button onClick={() => navigate(`/deal/${d.deal_id}`)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700">
                                <ExternalLink className="w-3 h-3 text-slate-400" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Risk Profile */}
            <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-[#45C04A]" />
                <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('portfolio.riskProfile')}</h3>
              </div>
              <div className="flex items-center justify-between mb-4">
                <div className="text-center">
                  <p className="text-xl font-bold text-green-500">{data.healthy_count}</p>
                  <p className="text-[10px] text-slate-400">{t('portfolio.healthy')}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-red-500">{data.at_risk_count}</p>
                  <p className="text-[10px] text-slate-400">{t('portfolio.atRisk')}</p>
                </div>
              </div>
              <div className="space-y-2">
                {["exceptional", "strong", "good", "average", "needs_review", "high_risk", "avoid"].map(band => {
                  const d = rp[band] || { count: 0, percent: 0 };
                  return <RiskBar key={band} band={band} count={d.count} percent={d.percent} />;
                })}
              </div>
            </div>

            {/* Diversification */}
            <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-[#45C04A]" />
                <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('portfolio.diversification')}</h3>
              </div>
              {div.concentration_risk && (
                <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/30">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs text-amber-600 dark:text-amber-400">{t('portfolio.concentrationWarning')}</span>
                </div>
              )}
              <h5 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('portfolio.byCity')}</h5>
              <div className="space-y-1.5 mb-4">
                {Object.entries(div.cities || {}).map(([city, count]) => (
                  <div key={city} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-300 truncate">{city}</span>
                    <span className="font-semibold text-[#12355B] dark:text-white ml-2">{count}</span>
                  </div>
                ))}
              </div>
              <h5 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('portfolio.byType')}</h5>
              <div className="flex gap-2">
                {Object.entries(div.unit_distribution || {}).map(([type, count]) => (
                  <div key={type} className="flex-1 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-2.5 text-center">
                    <p className="text-lg font-bold text-[#12355B] dark:text-white">{count}</p>
                    <p className="text-[10px] text-slate-400 capitalize">{t(`portfolio.type_${type}`, type.replace('_', ' '))}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Performers */}
            <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('portfolio.topPerformers')}</h3>
              </div>
              <div className="space-y-2">
                {(data.top_performers || []).map((d, i) => (
                  <button key={d.deal_id} onClick={() => navigate(`/deal/${d.deal_id}`)}
                    className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left">
                    <span className="w-6 h-6 rounded-full bg-green-500/10 text-green-500 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#12355B] dark:text-white truncate">{d.address}</p>
                      <p className="text-[10px] text-slate-400">{t('portfolioCompare.scoreLabel')} {fmt.number(d.deal_score)} &middot; CF {fmt.money0(d.monthly_cash_flow)}{t('portfolioCompare.perMonthSuffix')}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
